import { filterClasses } from "@swivl-mono/helpers";
import { BotSequenceConfigEffective, BotSequenceMode } from "@swivl-mono/types";
import { Tooltip } from "@swivl-mono/ui";

import { SequenceScopeKind } from "./sequenceScope";

interface SequenceStatusChipProps {
  effective: BotSequenceConfigEffective | undefined;
  scopeKind: SequenceScopeKind;
  /** The type has staged, unsaved edits. */
  dirty: boolean;
}

interface ChipStyle {
  label: string;
  dot: string;
  text: string;
  tooltip?: string;
}

/** Nothing saved at any scope is the default, Off (first-class defaults, decision 56). */
function styleForMode(mode: BotSequenceMode | null): ChipStyle {
  switch (mode) {
    case BotSequenceMode.ENABLED:
      return { label: "On", dot: "bg-teal-500", text: "text-teal-700" };
    case BotSequenceMode.SIMULATED:
      return { label: "Simulated", dot: "bg-purple-400", text: "text-purple-700", tooltip: "Decisions are logged as if live, nothing is sent." };
    case BotSequenceMode.DISABLED:
      return { label: "Off", dot: "bg-grey-400", text: "text-grey-600" };
    default:
      return {
        label: "Off",
        dot: "bg-grey-300",
        text: "text-grey-500",
        tooltip: "The default. Nothing is booked for this type until it is turned on."
      };
  }
}

/** On / Simulated / Off / Not configured beside each type in the left column, with an inherited marker and an unsaved dot. */
export function SequenceStatusChip({ effective, scopeKind, dirty }: SequenceStatusChipProps): React.ReactElement {
  const style = styleForMode(effective?.mode.value ?? null);
  const inheritedFrom =
    effective && effective.mode.value !== null && effective.mode.source !== scopeKind && effective.mode.source !== "default"
      ? effective.mode.source
      : null;

  const chip = (
    <span className={filterClasses("body-xs inline-flex items-center gap-1.5 font-semibold", style.text)}>
      <span className={filterClasses("inline-block size-2 rounded-full", style.dot)} />
      {style.label}
      {inheritedFrom && (
        <span
          className={filterClasses(
            "rounded-xs px-1 py-0.5 font-normal",
            inheritedFrom === "group" ? "bg-purple-100 text-purple-700" : "bg-teal-100 text-teal-700"
          )}
        >
          From {inheritedFrom === "group" ? "Group" : "Org"}
        </span>
      )}
      {dirty && <span className="inline-block size-2 rounded-full bg-orange-500" title="Unsaved changes" />}
    </span>
  );

  return style.tooltip ? <Tooltip content={style.tooltip}>{chip}</Tooltip> : chip;
}
